/**
 * ENHANCED TYPING ANALYZER - COMPONENT-LEVEL INTEGRATION
 * Monitors both input-specific AND component-wide events for comprehensive detection
 * OPTIMIZED VERSION: Improved performance and memory management
 */

import React from "react";

const ANALYSIS_CONFIG = {
  enableVerboseLogging: false, // ⚠️ OPTIMIZED: Disabled by default to reduce console overhead
  maxKeystrokeEvents: 1000, // ⚠️ OPTIMIZED: Reduced from 5000 to prevent memory bloat
  maxPasteEvents: 50, // ⚠️ OPTIMIZED: Reduced from 100
  analysisInterval: 200, // ⚠️ OPTIMIZED: Increased from 50 to reduce CPU overhead

  // Global monitoring settings - optimized
  ENABLE_GLOBAL_COPY_DETECTION: true,
  ENABLE_QUESTION_COPY_DETECTION: true,
  COPY_CORRELATION_WINDOW: 30000, // 30 seconds to correlate copy->paste

  // ⚠️ NEW: Performance optimization settings
  CLEANUP_INTERVAL: 60000, // Cleanup every 60 seconds instead of 30
  MAX_GLOBAL_COPY_EVENTS: 50, // Limit global copy tracking
  MAX_EXTERNAL_INTERACTIONS: 100, // Limit external interaction tracking
  DEBOUNCE_ANALYSIS: 1000, // Debounce real-time analysis
};

class TypingAnalyzer {
  constructor(options = {}) {
    // Configuration
    this.config = {
      ...ANALYSIS_CONFIG,
      ...options,
    };

    // Core tracking data
    this.typingData = {
      keystrokes: [],
      pasteEvents: [],
      focusEvents: [],
      copyEvents: [],
      selectionEvents: [],
      startTime: Date.now(),
      sessionId: this.generateSessionId(),
      isTracking: false,
    };

    // Enhanced tracking for component-level events
    this.componentData = {
      globalCopyEvents: [],
      questionCopyEvents: [],
      externalInteractions: [],
      clipboardHistory: [],
    };

    // Element references
    this.targetElement = null;
    this.componentContainer = null;

    // ⚠️ OPTIMIZED: Performance monitoring
    this.cleanupInterval = null;
    this.lastAnalysisTime = 0;
    this.analysisDebounceTimer = null;

    // Bound methods for event listeners
    this.boundHandlers = {
      input: this.handleInput.bind(this),
      paste: this.handlePaste.bind(this),
      copy: this.handleCopy.bind(this),
      cut: this.handleCut.bind(this),
      focus: this.handleFocus.bind(this),
      blur: this.handleBlur.bind(this),
      keydown: this.handleKeyDown.bind(this),
      keyup: this.handleKeyUp.bind(this),
      mouseup: this.handleMouseUp.bind(this),
      beforeunload: this.handleBeforeUnload.bind(this),
    };

    // Additional bound handlers for global events
    this.boundGlobalHandlers = {
      globalCopy: this.handleGlobalCopy.bind(this),
      globalCut: this.handleGlobalCut.bind(this),
      windowFocus: this.handleWindowFocus.bind(this),
      windowBlur: this.handleWindowBlur.bind(this),
      visibilityChange: this.handleVisibilityChange.bind(this),
    };

    this.log("Enhanced TypingAnalyzer initialized", {
      sessionId: this.typingData.sessionId,
      globalMonitoring: this.config.ENABLE_GLOBAL_COPY_DETECTION,
    });

    // // ⚠️ OPTIMIZED: Enable performance monitoring for this instance
    // if (process.env.NODE_ENV === "development") {
    //   performanceMonitor.monitorTypingAnalyzer(this);
    // }
  }

  /**
   * Enhanced start tracking - accepts component container for global monitoring
   * @param {HTMLElement} inputElement - The input/textarea element (optional)
   * @param {HTMLElement} componentContainer - The parent component container (optional)
   * @returns {boolean} Success status
   */
  startTracking(inputElement = null, componentContainer = null) {
    // Stop any existing tracking
    this.stopTracking();

    // Set up input-specific tracking (existing functionality)
    this.targetElement = inputElement;
    this.componentContainer = componentContainer;
    this.typingData.isTracking = true;
    this.typingData.startTime = Date.now();

    // Setup input-specific listeners (will skip if targetElement is null)
    this.setupInputEventListeners();

    // Setup component-level and global listeners
    if (componentContainer) {
      this.setupComponentEventListeners();
    }
    this.setupGlobalEventListeners();

    // this.setupPerformanceMonitoring();

    this.log("Started enhanced tracking", {
      hasInputElement: !!inputElement,
      hasComponentContainer: !!componentContainer,
      globalMonitoring: this.config.ENABLE_GLOBAL_COPY_DETECTION,
    });

    return true;
  }

  /**
   * Check if tracking is active
   * @returns {boolean}
   */
  isTracking() {
    return this.typingData.isTracking;
  }
  /**
   * Setup input-specific event listeners (existing functionality)
   */
  setupInputEventListeners() {
    if (!this.targetElement) return;

    // Core typing events (unchanged)
    this.targetElement.addEventListener("input", this.boundHandlers.input);
    this.targetElement.addEventListener("keydown", this.boundHandlers.keydown);
    this.targetElement.addEventListener("keyup", this.boundHandlers.keyup);
    this.targetElement.addEventListener("paste", this.boundHandlers.paste);
    this.targetElement.addEventListener("focus", this.boundHandlers.focus);
    this.targetElement.addEventListener("blur", this.boundHandlers.blur);
  }

  /**
   * Setup component-level event listeners (NEW)
   */
  setupComponentEventListeners() {
    if (!this.componentContainer) return;

    // Monitor copy events within the component
    this.componentContainer.addEventListener(
      "copy",
      this.boundGlobalHandlers.globalCopy,
    );
    this.componentContainer.addEventListener(
      "cut",
      this.boundGlobalHandlers.globalCut,
    );

    this.log("Component-level listeners attached", {
      componentId: this.componentContainer.id || "unnamed",
    });
  }

  /**
   * Setup global event listeners (NEW)
   */
  setupGlobalEventListeners() {
    if (!this.config.ENABLE_GLOBAL_COPY_DETECTION) return;

    // Global clipboard monitoring
    document.addEventListener("copy", this.boundGlobalHandlers.globalCopy);
    document.addEventListener("cut", this.boundGlobalHandlers.globalCut);

    // Window focus monitoring
    window.addEventListener("focus", this.boundGlobalHandlers.windowFocus);
    window.addEventListener("blur", this.boundGlobalHandlers.windowBlur);

    // Page visibility monitoring
    document.addEventListener(
      "visibilitychange",
      this.boundGlobalHandlers.visibilityChange,
    );

    this.log("Global listeners attached");
  }

  /**
   * Handle global copy events (OPTIMIZED)
   */
  handleGlobalCopy(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    // ⚠️ OPTIMIZED: Prevent duplicate detection with early return
    if (
      this.componentContainer &&
      this.componentContainer.contains(event.target) &&
      event.currentTarget === document
    ) {
      return;
    }

    const currentTime = Date.now();
    const relativeTime = currentTime - this.typingData.startTime;

    // Determine copy source
    const copySource = this.determineCopySource(event);
    const copiedContent = this.getSelectedText();

    // ⚠️ OPTIMIZED: Skip processing very small selections to reduce overhead
    if (copiedContent.length < 3) return;

    // ✅ NEW: Check if copy is from the target element (editor itself)
    const isFromTargetElement = this.isEventFromTargetElement(event);

    const globalCopyData = {
      timestamp: relativeTime,
      type: "global_copy",
      source: copySource,
      contentLength: copiedContent.length,

      // ⚠️ OPTIMIZED: Simplified content analysis to reduce CPU usage
      isQuestionText:
        copySource === "question_text" ||
        this.isQuestionTextCopied(copiedContent, copySource),
      hasCodePatterns: /[{}();[\]<>]/.test(copiedContent),
      hasFormatting: /[\n\t\r]/.test(copiedContent),

      // Context (minimized)
      targetElement: event.target.tagName || "unknown",
      targetId: event.target.id || "unnamed",

      // ✅ NEW: Flag to identify same-element copy-paste
      isFromTargetElement: isFromTargetElement,

      // Risk indicators
      riskLevel: this.assessCopyRiskLevel(copySource, copiedContent),
    };

    // ⚠️ OPTIMIZED: Early limit check to prevent array growth
    if (
      this.componentData.globalCopyEvents.length >=
      this.config.MAX_GLOBAL_COPY_EVENTS
    ) {
      this.componentData.globalCopyEvents.shift(); // Remove oldest
    }
    this.componentData.globalCopyEvents.push(globalCopyData);

    // Special handling for question copying (only if not from target element)
    if (globalCopyData.isQuestionText && !isFromTargetElement) {
      if (this.componentData.questionCopyEvents.length >= 25) {
        this.componentData.questionCopyEvents.shift(); // Remove oldest
      }
      this.componentData.questionCopyEvents.push({
        ...globalCopyData,
        type: "question_copy",
        severity: "high",
      });
    }

    this.log("Global copy detected", {
      source: copySource,
      isQuestion: globalCopyData.isQuestionText,
      length: copiedContent.length,
      risk: globalCopyData.riskLevel,
      isFromTargetElement: isFromTargetElement,
    });
  }

  /**
   * Handle global cut events (NEW)
   */
  handleGlobalCut(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    // ⚠️ FIX: Prevent duplicate detection for cut events too
    if (
      this.componentContainer &&
      this.componentContainer.contains(event.target) &&
      event.currentTarget === document
    ) {
      return;
    }

    // Process as copy event with cut type
    this.handleGlobalCopy({ ...event, type: "cut" });
  }

  /**
   * Handle window focus changes (OPTIMIZED)
   */
  handleWindowFocus(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const relativeTime = Date.now() - this.typingData.startTime;

    // ⚠️ OPTIMIZED: Early limit check
    if (
      this.componentData.externalInteractions.length >=
      this.config.MAX_EXTERNAL_INTERACTIONS
    ) {
      this.componentData.externalInteractions.shift();
    }

    this.componentData.externalInteractions.push({
      timestamp: relativeTime,
      type: "window_focus",
      action: "gained",
    });
  }

  /**
   * Handle window blur (OPTIMIZED)
   */
  handleWindowBlur(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const relativeTime = Date.now() - this.typingData.startTime;

    // ⚠️ OPTIMIZED: Early limit check
    if (
      this.componentData.externalInteractions.length >=
      this.config.MAX_EXTERNAL_INTERACTIONS
    ) {
      this.componentData.externalInteractions.shift();
    }

    this.componentData.externalInteractions.push({
      timestamp: relativeTime,
      type: "window_blur",
      action: "lost",
    });
  }

  /**
   * Handle page visibility changes (OPTIMIZED)
   */
  handleVisibilityChange(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const relativeTime = Date.now() - this.typingData.startTime;

    // ⚠️ OPTIMIZED: Early limit check
    if (
      this.componentData.externalInteractions.length >=
      this.config.MAX_EXTERNAL_INTERACTIONS
    ) {
      this.componentData.externalInteractions.shift();
    }

    this.componentData.externalInteractions.push({
      timestamp: relativeTime,
      type: "visibility_change",
      hidden: document.hidden,
    });
  }

  /**
   * Determine the source of a copy operation (NEW)
   */
  determineCopySource(event) {
    const target = event.target;

    // Check if it's from the input element
    if (target === this.targetElement) {
      return "input_element";
    }

    // Check if it's from question area
    if (this.isQuestionElement(target)) {
      return "question_text";
    }

    // Check if it's from component container
    if (this.componentContainer && this.componentContainer.contains(target)) {
      return "component_area";
    }

    // External source
    return "external_source";
  }

  /**
   * Check if target element contains question text (ENHANCED)
   */
  isQuestionElement(element) {
    if (!element) return false;

    // ⚠️ ENHANCED: More comprehensive question container identifiers
    const questionIndicators = [
      "question",
      "prompt",
      "instructions",
      "problem",
      "task",
      "description",
      // ⚠️ ADD: More specific indicators for better detection
      "quest",
      "ask",
      "prompt",
      "problem-statement",
      "instructions-text",
      "problem-desc",
      "task-desc",
      "question-text",
      "prompt-text",
      "challenge",
      "exercise",
      "assignment",
      "requirement",
      "scenario",
    ];

    const elementId = (element.id || "").toLowerCase();
    const elementClass = (element.className || "").toLowerCase();
    const parentId = (element.parentElement?.id || "").toLowerCase();
    const parentClass = (element.parentElement?.className || "").toLowerCase(); // ⚠️ ADD: Check parent class too

    // ⚠️ ENHANCED: Also check grandparent for deeply nested question elements
    const grandparentId = (
      element.parentElement?.parentElement?.id || ""
    ).toLowerCase();
    const grandparentClass = (
      element.parentElement?.parentElement?.className || ""
    ).toLowerCase();

    return questionIndicators.some(
      (indicator) =>
        elementId.includes(indicator) ||
        elementClass.includes(indicator) ||
        parentId.includes(indicator) ||
        parentClass.includes(indicator) || // ⚠️ NEW: Check parent class
        grandparentId.includes(indicator) || // ⚠️ NEW: Check grandparent ID
        grandparentClass.includes(indicator), // ⚠️ NEW: Check grandparent class
    );
  }

  /**
   * Check if copied content matches question text (NEW)
   */
  isQuestionTextCopied(copiedContent, source) {
    if (source === "question_text") return true;

    // Additional heuristics for question text detection
    const questionKeywords = [
      "write a function",
      "implement",
      "explain",
      "describe",
      "what is",
      "how does",
      "why",
      "analyze",
      "compare",
    ];

    const content = copiedContent.toLowerCase();
    return (
      questionKeywords.some((keyword) => content.includes(keyword)) &&
      copiedContent.length > 50
    ); // Substantial content
  }

  /**
   * Assess risk level of copy operation (NEW)
   */
  assessCopyRiskLevel(source, content) {
    if (source === "question_text" && content.length > 20) {
      return "high"; // Copying question is highly suspicious
    }

    if (source === "external_source" && content.length > 100) {
      return "medium"; // Large external copy
    }

    if (source === "component_area" && content.length > 50) {
      return "medium"; // Component area copy
    }

    return "low";
  }

  /**
   * Get currently selected text (NEW)
   */
  getSelectedText() {
    const selection = window.getSelection();
    return selection ? selection.toString() : "";
  }

  /**
   * ✅ NEW: Check if event is from the target element (editor)
   * Works for both Monaco Editor and SunEditor
   */
  isEventFromTargetElement(event) {
    if (!this.targetElement || !event.target) return false;

    const target = event.target;

    // Direct match
    if (target === this.targetElement) return true;

    // Check if target is contained within targetElement
    if (this.targetElement.contains && this.targetElement.contains(target)) {
      return true;
    }

    // For Monaco Editor: check if target is within the editor's DOM node
    // For SunEditor: check if target is within the sun-editor container
    try {
      let currentElement = target;
      while (currentElement && currentElement !== document.body) {
        if (currentElement === this.targetElement) {
          return true;
        }
        currentElement = currentElement.parentElement;
      }
    } catch (e) {
      // Silently handle any DOM traversal errors
    }

    return false;
  }

  /**
   * Enhanced paste event handler - now correlates with copy events
   */
  handlePaste(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const currentTime = Date.now();
    const relativeTime = currentTime - this.typingData.startTime;

    // Capture paste data for analysis
    const pastedText = event.clipboardData?.getData("text") || "";
    const beforeLength = this.getElementValue().length;

    // ✅ NEW: Check if paste is to the target element (editor)
    const isPasteToTargetElement = this.isEventFromTargetElement(event);

    const pasteData = {
      timestamp: relativeTime,
      type: "paste",
      pastedLength: pastedText.length,
      beforeLength: beforeLength,
      hasCodePatterns: /[{}();]/.test(pastedText),
      hasFormatting: pastedText.includes("\n") || pastedText.includes("\t"),
      hasSpecialChars: /[^\w\s]/.test(pastedText),
      sourceIndicators: this.analyzeClipboardSource(pastedText),
      // ✅ NEW: Flag to identify same-element paste
      isPasteToTargetElement: isPasteToTargetElement,
    };

    // NEW: Correlate with recent copy events
    const correlatedCopy = this.findCorrelatedCopyEvent(relativeTime);

    if (correlatedCopy) {
      // ✅ NEW: Check if both copy and paste are from/to the same target element
      const isSameElementCopyPaste =
        correlatedCopy.isFromTargetElement && isPasteToTargetElement;

      pasteData.correlatedCopy = {
        source: correlatedCopy.source,
        isQuestionText: correlatedCopy.isQuestionText,
        timeBetween: relativeTime - correlatedCopy.timestamp,
        riskLevel: correlatedCopy.riskLevel,
        // ✅ NEW: Flag for same-element copy-paste
        isSameElementCopyPaste: isSameElementCopyPaste,
      };

      // ✅ MODIFIED: Only flag as suspicious if NOT same-element copy-paste
      if (correlatedCopy.isQuestionText && !isSameElementCopyPaste) {
        pasteData.riskLevel = "critical";
        pasteData.isQuestionPaste = true;
      } else if (isSameElementCopyPaste) {
        // ✅ NEW: Mark as safe (low risk) for same-element copy-paste
        pasteData.riskLevel = "low";
        this.log("Same-element copy-paste detected (safe)", {
          source: correlatedCopy.source,
          pastedLength: pastedText.length,
        });
      }
    }

    this.typingData.pasteEvents.push(pasteData);

    this.log("Enhanced paste event detected", {
      pastedLength: pastedText.length,
      hasCorrelation: !!correlatedCopy,
      isQuestionPaste: pasteData.isQuestionPaste || false,
      isSameElementPaste:
        pasteData.correlatedCopy?.isSameElementCopyPaste || false,
    });
  }

  /**
   * Find correlated copy event for a paste (NEW)
   */
  findCorrelatedCopyEvent(pasteTimestamp) {
    // Look for copy events within correlation window
    const correlationWindow = this.config.COPY_CORRELATION_WINDOW;

    return this.componentData.globalCopyEvents
      .filter((copy) => pasteTimestamp - copy.timestamp <= correlationWindow)
      .sort((a, b) => b.timestamp - a.timestamp)[0]; // Most recent
  }

  /**
   * Core missing methods from original implementation
   */

  // Handle input events (OPTIMIZED main typing detection)
  handleInput(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const currentTime = Date.now();
    const currentValue = this.getElementValue();
    const relativeTime = currentTime - this.typingData.startTime;

    // ⚠️ OPTIMIZED: Early limit check to prevent memory bloat
    if (this.typingData.keystrokes.length >= this.config.maxKeystrokeEvents) {
      this.typingData.keystrokes.shift(); // Remove oldest keystroke
    }

    this.typingData.keystrokes.push({
      timestamp: relativeTime,
      type: "input",
      length: currentValue.length,
      inputType: event.inputType || "unknown",
      isComposition: event.isComposing || false,
    });

    // ⚠️ OPTIMIZED: Debounced real-time analysis to reduce CPU overhead
    this.scheduleAnalysis();
  }

  // ⚠️ NEW: Debounced analysis scheduling
  scheduleAnalysis() {
    if (this.analysisDebounceTimer) {
      clearTimeout(this.analysisDebounceTimer);
    }

    this.analysisDebounceTimer = setTimeout(() => {
      if (this.typingData.keystrokes.length >= 10) {
        this.performQuickAnalysis();
      }
    }, this.config.DEBOUNCE_ANALYSIS);
  }

  // Handle copy events
  handleCopy(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const currentTime = Date.now();
    const relativeTime = currentTime - this.typingData.startTime;

    this.typingData.copyEvents = this.typingData.copyEvents || [];
    this.typingData.copyEvents.push({
      timestamp: relativeTime,
      type: "copy",
    });
  }

  // Handle cut events
  handleCut(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const currentTime = Date.now();
    const relativeTime = currentTime - this.typingData.startTime;

    this.typingData.copyEvents = this.typingData.copyEvents || [];
    this.typingData.copyEvents.push({
      timestamp: relativeTime,
      type: "cut",
    });
  }

  // Handle focus events
  handleFocus(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const currentTime = Date.now();
    const relativeTime = currentTime - this.typingData.startTime;

    this.typingData.focusEvents.push({
      timestamp: relativeTime,
      type: "focus",
      action: "gained",
    });
  }

  // Handle blur events
  handleBlur(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const currentTime = Date.now();
    const relativeTime = currentTime - this.typingData.startTime;

    this.typingData.focusEvents.push({
      timestamp: relativeTime,
      type: "blur",
      action: "lost",
    });
  }

  // Handle keydown events
  handleKeyDown(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    // Track special keys without logging content
    const currentTime = Date.now();
    const relativeTime = currentTime - this.typingData.startTime;

    if (["Backspace", "Delete", "Enter", "Tab"].includes(event.key)) {
      this.typingData.keystrokes.push({
        timestamp: relativeTime,
        type: "keydown",
        key: event.key,
        length: this.getElementValue().length,
      });
    }
  }

  // Handle keyup events
  handleKeyUp(event) {
    // Currently not needed but kept for future extensions
  }

  // Handle mouse up events for selection tracking
  handleMouseUp(event) {
    if (!this.typingData || !this.typingData.isTracking) return;

    const selection = this.getSelection();
    if (selection.length > 0) {
      const currentTime = Date.now();
      const relativeTime = currentTime - this.typingData.startTime;

      this.typingData.selectionEvents = this.typingData.selectionEvents || [];
      this.typingData.selectionEvents.push({
        timestamp: relativeTime,
        type: "selection",
        length: selection.length,
      });
    }
  }

  // Handle before unload
  handleBeforeUnload(event) {
    this.stopTracking();
  }

  // Get element value (works with regular inputs and rich text editors)
  getElementValue() {
    if (!this.targetElement) return "";

    // Handle different types of elements
    if (this.targetElement.value !== undefined) {
      return this.targetElement.value;
    } else if (this.targetElement.textContent !== undefined) {
      return this.targetElement.textContent;
    } else if (this.targetElement.innerText !== undefined) {
      return this.targetElement.innerText;
    } else if (this.targetElement.innerHTML !== undefined) {
      // For rich text editors, strip HTML tags for analysis
      const div = document.createElement("div");
      div.innerHTML = this.targetElement.innerHTML;
      return div.textContent || div.innerText || "";
    }

    return "";
  }

  // Get current selection
  getSelection() {
    const selection = window.getSelection();
    return selection ? selection.toString() : "";
  }

  // Analyze clipboard source characteristics (privacy-safe)
  analyzeClipboardSource(text) {
    const indicators = [];

    if (text.length > 1000) indicators.push("large-text");
    if (/[{}();[\]<>]/.test(text)) indicators.push("code-structure");
    if (/[\n\t]/.test(text)) indicators.push("formatted-text");

    return indicators;
  }

  /**
   * Get suspicious events for reporting
   * @returns {Array} List of cheating flags strings
   */
  getSuspiciousEvents() {
    const flags = [];

    // Check for high severity question copies
    if (this.componentData.questionCopyEvents.length > 0) {
      flags.push("Question Copying Detected");
    }

    // Check for risky paste events
    const riskyPastes = this.typingData.pasteEvents.filter(
      (p) => p.riskLevel === "critical" || p.isQuestionPaste,
    );
    if (riskyPastes.length > 0) {
      flags.push("Suspicious Paste Detected");
    }

    // Check for global copy events that are question text
    const questionCopies = this.componentData.globalCopyEvents.filter(
      (e) => e.isQuestionText,
    );
    if (questionCopies.length > 0) {
      flags.push("Question Text Copy Detected");
    }

    // Deduplicate
    return [...new Set(flags)];
  }

  // Perform quick real-time analysis (OPTIMIZED)
  performQuickAnalysis() {
    if (this.typingData.keystrokes.length < 10) return null;

    // ⚠️ OPTIMIZED: Throttle analysis to prevent excessive CPU usage
    const now = Date.now();
    if (now - this.lastAnalysisTime < this.config.DEBOUNCE_ANALYSIS) {
      return null;
    }
    this.lastAnalysisTime = now;

    const analysis = {
      suspiciousActivity: false,
      indicators: [],
      confidence: 0,
      metrics: {},
    };

    // ⚠️ OPTIMIZED: Cached calculation to avoid repeated DOM queries
    const currentLength = this.getElementValue().length;
    const totalPasted = this.typingData.pasteEvents.reduce(
      (sum, event) => sum + (event.pastedLength || 0),
      0,
    );
    const pastePercentage =
      currentLength > 0 ? Math.min(1.0, totalPasted / currentLength) : 0;

    analysis.metrics.pastePercentage = Math.round(pastePercentage * 100);
    analysis.metrics.totalPasteEvents = this.typingData.pasteEvents.length;
    analysis.metrics.totalKeystrokes = this.typingData.keystrokes.length;
    analysis.metrics.focusLossCount = this.typingData.focusEvents.filter(
      (e) => e.action === "lost",
    ).length;

    // High paste activity check
    if (pastePercentage > 0.5) {
      analysis.suspiciousActivity = true;
      analysis.indicators.push(
        `High paste activity: ${Math.round(pastePercentage * 100)}%`,
      );
      analysis.confidence = Math.min(90, pastePercentage * 100);
    }

    return analysis;
  }

  // Generate unique session ID
  generateSessionId() {
    return `typing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Privacy-compliant logging
  log(message, data = {}) {
    if (this.config.enableVerboseLogging) {
      console.log(`[EnhancedTypingAnalyzer] ${message}`, data); // eslint-disable-line no-console
    }
  }

  // Stop tracking and cleanup
  stopTracking() {
    if (
      !this.typingData ||
      !this.typingData.isTracking ||
      !this.targetElement
    ) {
      return;
    }

    this.removeEventListeners();
    this.typingData.isTracking = false;

    this.log("Stopped enhanced tracking");
  }

  // Setup performance monitoring (OPTIMIZED)
  // setupPerformanceMonitoring() {
  //   // ⚠️ OPTIMIZED: Increased cleanup interval to reduce overhead
  //   this.cleanupInterval = setInterval(() => {
  //     if (!this.typingData) return;

  //     // Existing cleanup for input-specific data with optimized limits
  //     if (this.typingData.keystrokes.length > this.config.maxKeystrokeEvents) {
  //       const keepCount = Math.floor(this.config.maxKeystrokeEvents * 0.8); // Keep 80%
  //       this.typingData.keystrokes = this.typingData.keystrokes.slice(
  //         -keepCount
  //       );
  //     }

  //     if (this.typingData.pasteEvents.length > this.config.maxPasteEvents) {
  //       const keepCount = Math.floor(this.config.maxPasteEvents * 0.8);
  //       this.typingData.pasteEvents = this.typingData.pasteEvents.slice(
  //         -keepCount
  //       );
  //     }

  //     // ⚠️ OPTIMIZED: More aggressive component data cleanup
  //     if (this.componentData) {
  //       // Limit global copy events with improved cleanup
  //       if (
  //         this.componentData.globalCopyEvents.length >
  //         this.config.MAX_GLOBAL_COPY_EVENTS
  //       ) {
  //         const keepCount = Math.floor(
  //           this.config.MAX_GLOBAL_COPY_EVENTS * 0.7
  //         );
  //         this.componentData.globalCopyEvents =
  //           this.componentData.globalCopyEvents.slice(-keepCount);
  //       }

  //       // Limit question copy events
  //       if (this.componentData.questionCopyEvents.length > 25) {
  //         this.componentData.questionCopyEvents =
  //           this.componentData.questionCopyEvents.slice(-20);
  //       }

  //       // Limit external interactions tracking
  //       if (
  //         this.componentData.externalInteractions.length >
  //         this.config.MAX_EXTERNAL_INTERACTIONS
  //       ) {
  //         const keepCount = Math.floor(
  //           this.config.MAX_EXTERNAL_INTERACTIONS * 0.7
  //         );
  //         this.componentData.externalInteractions =
  //           this.componentData.externalInteractions.slice(-keepCount);
  //       }

  //       // Limit clipboard history
  //       if (this.componentData.clipboardHistory.length > 15) {
  //         // Reduced from 30
  //         this.componentData.clipboardHistory =
  //           this.componentData.clipboardHistory.slice(-10);
  //       }

  //       // ⚠️ OPTIMIZED: Less frequent memory logging to reduce console overhead
  //       const shouldLogMemory = Date.now() % 120000 < 30000; // Log every 2 minutes for 30 seconds
  //       if (shouldLogMemory) {
  //         const memoryStats = {
  //           keystrokes: this.typingData.keystrokes.length,
  //           pasteEvents: this.typingData.pasteEvents.length,
  //           globalCopyEvents: this.componentData.globalCopyEvents.length,
  //           questionCopyEvents: this.componentData.questionCopyEvents.length,
  //           externalInteractions:
  //             this.componentData.externalInteractions.length,
  //           totalEvents:
  //             this.typingData.keystrokes.length +
  //             this.typingData.pasteEvents.length +
  //             this.componentData.globalCopyEvents.length +
  //             this.componentData.externalInteractions.length,
  //         };

  //         if (memoryStats.totalEvents > 0) {
  //           this.log("Enhanced analyzer memory stats", memoryStats);
  //         }
  //       }
  //     }
  //   }, this.config.CLEANUP_INTERVAL);
  // }

  // Reset analyzer state
  reset() {
    if (!this.typingData) return;

    this.typingData.keystrokes = [];
    this.typingData.pasteEvents = [];
    this.typingData.focusEvents = [];
    this.typingData.copyEvents = [];
    this.typingData.selectionEvents = [];
    this.typingData.startTime = Date.now();
    this.typingData.sessionId = this.generateSessionId();

    if (this.componentData) {
      this.componentData.globalCopyEvents = [];
      this.componentData.questionCopyEvents = [];
      this.componentData.externalInteractions = [];
      this.componentData.clipboardHistory = [];
    }

    this.lastAnalysisTime = 0;
    this.log("Enhanced TypingAnalyzer reset");
  }

  // Cleanup and destroy analyzer (OPTIMIZED)
  destroy() {
    this.stopTracking();

    // ⚠️ OPTIMIZED: Clear debounce timer
    if (this.analysisDebounceTimer) {
      clearTimeout(this.analysisDebounceTimer);
      this.analysisDebounceTimer = null;
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    // Log before clearing data
    this.log("Enhanced TypingAnalyzer destroyed");

    // ⚠️ OPTIMIZED: More thorough cleanup
    this.typingData = null;
    this.componentData = null;
    this.targetElement = null;
    this.componentContainer = null;
    this.boundHandlers = null;
    this.boundGlobalHandlers = null;
  }

  /**
   * Enhanced comprehensive analysis - includes global events
   */
  getComprehensiveAnalysis() {
    if (!this.typingData) {
      console.warn(
        "EnhancedTypingAnalyzer: Cannot get analysis, typingData is null",
      );
      return null;
    }

    const currentTime = Date.now();
    const totalDuration = currentTime - this.typingData.startTime;
    const currentValue = this.getElementValue();

    // Perform comprehensive analysis
    const pasteAnalysis = this.analyzePastePatterns();
    const typingAnalysis = this.analyzeTypingPatterns();
    const focusAnalysis = this.analyzeFocusPatterns();
    const qualityAnalysis = this.analyzeQualityVsTime(
      currentValue,
      totalDuration,
    );

    // NEW: Add global event analysis
    const globalEventAnalysis = this.analyzeGlobalEvents();
    const copyPasteCorrelations = this.analyzeCopyPasteCorrelations();

    return {
      // Core metrics
      totalDuration,
      totalCharacters: currentValue.length,
      keystrokeCount: this.typingData.keystrokes.length,
      pasteEventCount: this.typingData.pasteEvents.length,
      copyEventCount: (this.typingData.copyEvents || []).length,
      focusLossCount: this.typingData.focusEvents.filter(
        (e) => e.action === "lost",
      ).length,

      // Detailed analysis
      pasteAnalysis,
      typingAnalysis,
      focusAnalysis,
      qualityAnalysis,

      // NEW: Global event data
      globalEventAnalysis,
      copyPasteCorrelations,

      // Overall risk assessment
      riskScore: this.calculateEnhancedRiskScore(
        pasteAnalysis,
        typingAnalysis,
        focusAnalysis,
        qualityAnalysis,
        globalEventAnalysis,
        copyPasteCorrelations,
      ),

      // Metadata
      sessionId: this.typingData.sessionId,
      analysisVersion: "2.0-enhanced",
      timestamp: new Date().toISOString(),

      // Privacy compliance
      privacyCompliant: true,
    };
  }

  /**
   * Analyze global events (NEW)
   */
  analyzeGlobalEvents() {
    return {
      globalCopyCount: this.componentData.globalCopyEvents.length,
      questionCopyCount: this.componentData.questionCopyEvents.length,
      externalInteractionCount: this.componentData.externalInteractions.length,

      // Risk indicators
      hasQuestionCopying: this.componentData.questionCopyEvents.length > 0,
      hasHighRiskCopying: this.componentData.globalCopyEvents.some(
        (copy) => copy.riskLevel === "high",
      ),

      // Pattern analysis
      copySourceDistribution: this.analyzeCopySourceDistribution(),
      suspiciousPatternCount: this.countSuspiciousPatterns(),

      riskLevel: this.calculateGlobalEventRiskLevel(),
    };
  }

  /**
   * Missing analysis methods implementation
   */

  // Analyze paste patterns
  analyzePastePatterns() {
    // ✅ MODIFIED: Filter out same-element copy-paste events for risk calculation
    // but keep them in the data structure for transparency
    const suspiciousPasteEvents = this.typingData.pasteEvents.filter(
      (event) => !event.correlatedCopy?.isSameElementCopyPaste,
    );

    const totalPasted = suspiciousPasteEvents.reduce(
      (sum, event) => sum + (event.pastedLength || 0),
      0,
    );
    const currentLength = this.getElementValue().length;
    const rawPastePercentage =
      currentLength > 0 ? totalPasted / currentLength : 0;

    // ⚠️ CRITICAL: Cap paste percentage at 100% to prevent logical inconsistencies
    // This can happen when users paste content and then delete/edit it
    const pastePercentage = Math.min(1.0, rawPastePercentage);

    // ⚠️ CRITICAL: Safeguard for largestPaste calculation
    const pasteLengths = suspiciousPasteEvents.map((e) => e.pastedLength || 0);
    const largestPaste =
      pasteLengths.length > 0 ? Math.max(...pasteLengths) : 0;

    return {
      totalPasteEvents: this.typingData.pasteEvents.length, // Total count (includes same-element)
      totalPastedCharacters: totalPasted, // Only suspicious pastes
      pastePercentage: Math.round(pastePercentage * 100), // Based on suspicious pastes only
      rawPastePercentage: Math.round(rawPastePercentage * 100), // Original uncapped value for analysis
      largestPaste: largestPaste, // ⚠️ CRITICAL: Ensured safe calculation
      hasCodePatterns: suspiciousPasteEvents.some((e) => e.hasCodePatterns),
      hasFormatting: suspiciousPasteEvents.some((e) => e.hasFormatting),
      riskLevel:
        pastePercentage > 0.7
          ? "high"
          : pastePercentage > 0.3
            ? "medium"
            : "low",
    };
  }

  // Analyze typing patterns
  analyzeTypingPatterns() {
    const totalDuration = Date.now() - this.typingData.startTime;
    const avgTypingSpeed =
      this.typingData.keystrokes.length > 0
        ? this.getElementValue().length / (totalDuration / 1000)
        : 0;

    return {
      averageTypingSpeed: Math.round(avgTypingSpeed * 100) / 100,
      typingBursts: 0, // Simplified for now
      totalKeystrokes: this.typingData.keystrokes.length,
      backspaceCount: this.typingData.keystrokes.filter(
        (k) => k.key === "Backspace",
      ).length,
      riskLevel:
        avgTypingSpeed > 20 ? "high" : avgTypingSpeed > 10 ? "medium" : "low",
    };
  }

  // Analyze focus patterns
  analyzeFocusPatterns() {
    const focusLosses = this.typingData.focusEvents.filter(
      (e) => e.action === "lost",
    ).length;
    const totalFocusEvents = this.typingData.focusEvents.length;

    return {
      totalFocusEvents,
      focusLossCount: focusLosses,
      focusChangeFrequency:
        totalFocusEvents > 0 ? focusLosses / totalFocusEvents : 0,
      riskLevel: focusLosses > 10 ? "high" : focusLosses > 5 ? "medium" : "low",
    };
  }

  // Analyze quality vs time inconsistencies
  analyzeQualityVsTime(content, duration) {
    const wordCount = content
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    const avgWordsPerMinute = duration > 0 ? wordCount / (duration / 60000) : 0;

    // Simple quality indicators
    const hasProperStructure = /[.!?]/.test(content);
    const hasVariedVocabulary =
      new Set(content.toLowerCase().split(/\s+/)).size > wordCount * 0.7;

    return {
      wordCount,
      averageWordsPerMinute: Math.round(avgWordsPerMinute * 100) / 100,
      hasProperStructure,
      hasVariedVocabulary,
      qualityScore:
        (hasProperStructure ? 1 : 0) + (hasVariedVocabulary ? 1 : 0),
      riskLevel:
        avgWordsPerMinute > 100
          ? "high"
          : avgWordsPerMinute > 60
            ? "medium"
            : "low",
    };
  }

  // Analyze copy source distribution
  analyzeCopySourceDistribution() {
    const sources = {};
    this.componentData.globalCopyEvents.forEach((copy) => {
      sources[copy.source] = (sources[copy.source] || 0) + 1;
    });
    return sources;
  }

  // Count suspicious patterns
  countSuspiciousPatterns() {
    let count = 0;

    // High paste percentage
    const pasteAnalysis = this.analyzePastePatterns();
    if (pasteAnalysis.pastePercentage > 50) count++;

    // Question copying
    if (this.componentData.questionCopyEvents.length > 0) count++;

    // Multiple high-risk copies
    if (
      this.componentData.globalCopyEvents.filter((c) => c.riskLevel === "high")
        .length > 1
    )
      count++;

    // Frequent focus losses
    const focusLosses = this.typingData.focusEvents.filter(
      (e) => e.action === "lost",
    ).length;
    if (focusLosses > 5) count++;

    return count;
  }

  // Calculate global event risk level
  calculateGlobalEventRiskLevel() {
    const suspiciousCount = this.countSuspiciousPatterns();
    const hasQuestionCopying = this.componentData.questionCopyEvents.length > 0;

    if (hasQuestionCopying || suspiciousCount >= 3) return "high";
    if (suspiciousCount >= 2) return "medium";
    return "low";
  }

  // Enhanced risk score calculation
  calculateEnhancedRiskScore(
    pasteAnalysis,
    typingAnalysis,
    focusAnalysis,
    qualityAnalysis,
    globalEventAnalysis,
    copyPasteCorrelations,
  ) {
    let riskScore = 0;

    // Original risk factors (70% weight)
    if (pasteAnalysis.riskLevel === "high") riskScore += 30;
    else if (pasteAnalysis.riskLevel === "medium") riskScore += 15;

    if (typingAnalysis.riskLevel === "high") riskScore += 20;
    else if (typingAnalysis.riskLevel === "medium") riskScore += 10;

    if (focusAnalysis.riskLevel === "high") riskScore += 15;
    else if (focusAnalysis.riskLevel === "medium") riskScore += 7;

    if (qualityAnalysis.riskLevel === "high") riskScore += 5;
    else if (qualityAnalysis.riskLevel === "medium") riskScore += 2;

    // NEW: Enhanced risk factors (30% weight)
    if (globalEventAnalysis.riskLevel === "high") riskScore += 20;
    else if (globalEventAnalysis.riskLevel === "medium") riskScore += 10;

    if (copyPasteCorrelations.riskLevel === "critical") riskScore += 10;
    else if (copyPasteCorrelations.riskLevel === "high") riskScore += 5;

    return Math.min(100, riskScore);
  }

  /**
   * Analyze copy-paste correlations (NEW)
   */
  analyzeCopyPasteCorrelations() {
    // ✅ MODIFIED: Filter out same-element copy-paste from correlation analysis
    const correlations = this.typingData.pasteEvents
      .filter(
        (paste) =>
          paste.correlatedCopy && !paste.correlatedCopy.isSameElementCopyPaste,
      )
      .map((paste) => ({
        pasteTimestamp: paste.timestamp,
        copySource: paste.correlatedCopy.source,
        isQuestionPaste: paste.correlatedCopy.isQuestionText,
        timeBetween: paste.correlatedCopy.timeBetween,
      }));

    return {
      totalCorrelations: correlations.length,
      questionPasteCount: correlations.filter((c) => c.isQuestionPaste).length,
      averageTimeBetween:
        correlations.length > 0
          ? correlations.reduce((sum, c) => sum + c.timeBetween, 0) /
            correlations.length
          : 0,

      riskLevel:
        correlations.filter((c) => c.isQuestionPaste).length > 0
          ? "critical"
          : correlations.length > 2
            ? "high"
            : correlations.length > 0
              ? "medium"
              : "low",
    };
  }

  /**
   * Enhanced cleanup - includes global listeners
   * ✅ PERFORMANCE FIX: Added defensive null checks
   */
  removeEventListeners() {
    // Remove input-specific listeners
    if (this.targetElement && this.boundHandlers) {
      this.targetElement.removeEventListener("input", this.boundHandlers.input);
      this.targetElement.removeEventListener(
        "keydown",
        this.boundHandlers.keydown,
      );
      this.targetElement.removeEventListener("keyup", this.boundHandlers.keyup);
      this.targetElement.removeEventListener("paste", this.boundHandlers.paste);
      this.targetElement.removeEventListener("copy", this.boundHandlers.copy);
      this.targetElement.removeEventListener("cut", this.boundHandlers.cut);
      this.targetElement.removeEventListener("focus", this.boundHandlers.focus);
      this.targetElement.removeEventListener("blur", this.boundHandlers.blur);
      this.targetElement.removeEventListener(
        "mouseup",
        this.boundHandlers.mouseup,
      );
      window.removeEventListener(
        "beforeunload",
        this.boundHandlers.beforeunload,
      );
    }

    // ✅ PERFORMANCE FIX: Check boundGlobalHandlers before using
    if (!this.boundGlobalHandlers) return;

    // Remove component-level listeners
    if (this.componentContainer) {
      this.componentContainer.removeEventListener(
        "copy",
        this.boundGlobalHandlers.globalCopy,
      );
      this.componentContainer.removeEventListener(
        "cut",
        this.boundGlobalHandlers.globalCut,
      );
    }

    // Remove global listeners
    document.removeEventListener("copy", this.boundGlobalHandlers.globalCopy);
    document.removeEventListener("cut", this.boundGlobalHandlers.globalCut);
    window.removeEventListener("focus", this.boundGlobalHandlers.windowFocus);
    window.removeEventListener("blur", this.boundGlobalHandlers.windowBlur);
    document.removeEventListener(
      "visibilitychange",
      this.boundGlobalHandlers.visibilityChange,
    );
  }

  // ... rest of existing methods ...
}

/**
 * Enhanced React Hook with component-level integration (OPTIMIZED)
 */
export const useEnhancedTypingAnalyzer = (options = {}) => {
  const analyzerRef = React.useRef(null);
  const optionsRef = React.useRef(options);

  // ⚠️ OPTIMIZED: Memoize options to prevent unnecessary recreations
  React.useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // ⚠️ OPTIMIZED: Initialize analyzer only once
  const initializeAnalyzer = React.useCallback(() => {
    if (!analyzerRef.current) {
      analyzerRef.current = new TypingAnalyzer(optionsRef.current);
    }
    return analyzerRef.current;
  }, []);

  React.useEffect(() => {
    return () => {
      // ⚠️ OPTIMIZED: Ensure cleanup on unmount
      if (analyzerRef.current) {
        analyzerRef.current.destroy();
        analyzerRef.current = null;
      }
    };
  }, []);

  // ⚠️ OPTIMIZED: Memoized methods to prevent unnecessary re-renders
  const startTracking = React.useCallback(
    (inputElement, componentContainer = null) => {
      const analyzer = initializeAnalyzer();
      return analyzer.startTracking(inputElement, componentContainer);
    },
    [initializeAnalyzer],
  );

  const stopTracking = React.useCallback(() => {
    if (analyzerRef.current) {
      analyzerRef.current.stopTracking();
    }
  }, []);

  const getAnalysis = React.useCallback(() => {
    if (analyzerRef.current) {
      return analyzerRef.current.getComprehensiveAnalysis();
    }
    return null;
  }, []);

  const destroy = React.useCallback(() => {
    if (analyzerRef.current) {
      analyzerRef.current.destroy();
      analyzerRef.current = null;
    }
  }, []);

  const isTracking = React.useCallback(() => {
    return analyzerRef.current?.typingData?.isTracking || false;
  }, []);

  return React.useMemo(
    () => ({
      startTracking,
      stopTracking,
      getAnalysis,
      destroy,
      isTracking,
    }),
    [startTracking, stopTracking, getAnalysis, destroy, isTracking],
  );
};

/**
 * Original React Hook for backward compatibility (OPTIMIZED)
 */
export const useTypingAnalyzer = (options = {}) => {
  const analyzerRef = React.useRef(null);
  const optionsRef = React.useRef(options);

  // ⚠️ OPTIMIZED: Memoize options to prevent unnecessary recreations
  React.useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // ⚠️ OPTIMIZED: Initialize analyzer only once
  const initializeAnalyzer = React.useCallback(() => {
    if (!analyzerRef.current) {
      analyzerRef.current = new TypingAnalyzer(optionsRef.current);
    }
    return analyzerRef.current;
  }, []);

  React.useEffect(() => {
    return () => {
      // ⚠️ OPTIMIZED: Ensure cleanup on unmount
      if (analyzerRef.current) {
        analyzerRef.current.destroy();
        analyzerRef.current = null;
      }
    };
  }, []);

  // ⚠️ OPTIMIZED: Memoized methods
  const startTracking = React.useCallback(
    (element) => {
      const analyzer = initializeAnalyzer();
      return analyzer.startTracking(element);
    },
    [initializeAnalyzer],
  );

  const stopTracking = React.useCallback(() => {
    if (analyzerRef.current) {
      analyzerRef.current.stopTracking();
    }
  }, []);

  const getAnalysis = React.useCallback(() => {
    if (analyzerRef.current) {
      return analyzerRef.current.getComprehensiveAnalysis();
    }
    return null;
  }, []);

  const destroy = React.useCallback(() => {
    if (analyzerRef.current) {
      analyzerRef.current.destroy();
      analyzerRef.current = null;
    }
  }, []);

  const isTracking = React.useCallback(() => {
    return analyzerRef.current?.typingData?.isTracking || false;
  }, []);

  return React.useMemo(
    () => ({
      startTracking,
      stopTracking,
      getAnalysis,
      destroy,
      isTracking,
    }),
    [startTracking, stopTracking, getAnalysis, destroy, isTracking],
  );
};

export default TypingAnalyzer;
